// P1-16: read an uploaded intent list by its COLUMN HEADERS, not by column position.
//
// The importer used to call sheet_to_json with a `header` array, which tells SheetJS to
// ignore the real header row and assign those names to columns A-F positionally. The
// file's own headers were never read. So a correct file with its columns in a different
// order, or with any extra column in front, imported into the wrong fields silently --
// and since P1-12 takes Item # from row order, the resulting numbering was meaningless
// too.
//
// Everything here works on a plain array-of-arrays so it can be unit-tested without
// SheetJS fixtures. The caller does the parsing:
//   const aoa = xlsx.utils.sheet_to_json(worksheet, { header: 1, defval: '' });
//   const parsed = parseIntentRows(aoa);

// Serial identity is defined once, in scanMatch.js, and shared -- the importer's duplicate
// check and the scan screen's matching must agree about what "the same serial" means.
import { serialKey } from './scanMatch';

// The canonical template, in the order it is offered for download: the two required
// columns first, then everything else the importer understands.
const INTENT_LIST_COLUMNS = [
    { header: 'Serial Number', field: 'serial_number', required: true, example: 'ABC123456' },
    { header: 'Asset Type', field: 'asset_type', required: true, example: 'Laptop' },
    { header: 'Asset Tag', field: 'asset_tag', example: 'CC-00042' },
    { header: 'Description', field: 'description', example: 'Dell Latitude 5420' },
    { header: 'Notes', field: 'collection_notes', example: 'Left rear dock' },
    { header: 'Make', field: 'make', example: 'Dell' },
    { header: 'Model', field: 'model', example: 'Latitude 5420' },
    { header: 'Comments', field: 'comments', example: '' },
    { header: 'Reason To Dispose', field: 'reason_to_dispose', example: '' },
    { header: 'INC#', field: 'inc_number', example: '' }
];

const REQUIRED_FIELDS = INTENT_LIST_COLUMNS.filter(c => c.required).map(c => c.field);

// Accepted spellings for each column. The canonical header of every column is always
// accepted; these are the extra variants seen in real client files.
const HEADER_ALIASES = {
    serial_number: ['Serial', 'Serial #', 'SerialNumber', 'Serial No', 'Serial No.'],
    asset_type: ['AssetType', 'Type', 'Equipment Type'],
    asset_tag: ['AssetTag', 'Tag', 'Asset ID'],
    description: ['Desc', 'Asset Description'],
    collection_notes: ['Note', 'Collection Notes'],
    make: ['Manufacturer', 'Mfg', 'Brand'],
    model: ['Model #', 'Model Number', 'ModelNumber'],
    comments: ['Comment'],
    reason_to_dispose: ['Reason', 'Disposition', 'Reason for Disposal'],
    inc_number: ['INC', 'INC Number', 'Incident', 'Incident Number']
};

// Case- and whitespace-insensitive, and tolerant of the trailing punctuation people add.
const normalizeHeader = (header) => String(header ?? '')
    .replace(/ /g, ' ')          // non-breaking spaces paste in from Excel
    .trim()
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .replace(/[.:]+$/, '');

const HEADER_LOOKUP = (() => {
    const lookup = new Map();
    INTENT_LIST_COLUMNS.forEach(({ header, field }) => {
        lookup.set(normalizeHeader(header), field);
        (HEADER_ALIASES[field] || []).forEach(alias => lookup.set(normalizeHeader(alias), field));
    });
    return lookup;
})();

const fieldForHeader = (header) => HEADER_LOOKUP.get(normalizeHeader(header)) || null;

// How many cells in a row look like intent-list headers.
const scoreHeaderRow = (row) => (Array.isArray(row) ? row : [])
    .filter(cell => fieldForHeader(cell) !== null).length;

// Real spreadsheets often carry a title row or two above the headers, so don't assume
// row 0. Take the best-scoring row in the first few, requiring at least two recognised
// columns so a stray data row can't win.
const HEADER_SEARCH_DEPTH = 10;
const MIN_HEADER_MATCHES = 2;

const findHeaderRow = (aoa) => {
    const rows = Array.isArray(aoa) ? aoa : [];
    let bestIndex = -1;
    let bestScore = 0;
    rows.slice(0, HEADER_SEARCH_DEPTH).forEach((row, index) => {
        const score = scoreHeaderRow(row);
        if (score > bestScore) {
            bestScore = score;
            bestIndex = index;
        }
    });
    return bestScore >= MIN_HEADER_MATCHES ? bestIndex : -1;
};

const isBlankRow = (row) => (Array.isArray(row) ? row : [])
    .every(cell => cell == null || String(cell).trim() === '');

/**
 * Turn a sheet's array-of-arrays into intent-list rows keyed by database field.
 *
 * Returns:
 *   rows             - one object per data row, keyed by db field, in FILE ORDER
 *                      (P1-12 assigns Item # from this order, so it must be preserved)
 *   fields           - db fields actually present, in the file's column order
 *   headerRowIndex   - which row was used as the header row (-1 if none was found)
 *   unknownHeaders   - headers that matched nothing, so the user can be told what was
 *                      ignored rather than silently losing a column
 *   missingRequired  - required db fields with no column in the file
 */
const parseIntentRows = (aoa) => {
    const rows = Array.isArray(aoa) ? aoa : [];
    const headerRowIndex = findHeaderRow(rows);

    if (headerRowIndex === -1) {
        return {
            rows: [],
            fields: [],
            headerRowIndex: -1,
            unknownHeaders: [],
            missingRequired: REQUIRED_FIELDS
        };
    }

    const headerRow = rows[headerRowIndex];
    const unknownHeaders = [];
    // Column index -> db field. First occurrence of a field wins, so a duplicated
    // column can't overwrite the real one with a later blank.
    const columnFields = new Map();
    const seenFields = new Set();

    headerRow.forEach((cell, columnIndex) => {
        const field = fieldForHeader(cell);
        if (field === null) {
            const label = String(cell ?? '').trim();
            if (label !== '') unknownHeaders.push(label);
            return;
        }
        if (seenFields.has(field)) return;
        seenFields.add(field);
        columnFields.set(columnIndex, field);
    });

    const fields = [...columnFields.values()];

    const dataRows = rows.slice(headerRowIndex + 1)
        .filter(row => !isBlankRow(row))
        .map((row) => {
            const parsed = {};
            columnFields.forEach((field, columnIndex) => {
                const cell = row[columnIndex];
                parsed[field] = cell == null ? '' : String(cell).trim();
            });
            return parsed;
        });

    return {
        rows: dataRows,
        fields,
        headerRowIndex,
        unknownHeaders,
        missingRequired: REQUIRED_FIELDS.filter(field => !seenFields.has(field))
    };
};

// The mapping shape the backend's import endpoint expects: a dictionary keyed by db
// column whose values point back at themselves. Built from the fields actually found,
// so a file without a Make column doesn't claim to have one.
const mappingForFields = (fields) => (Array.isArray(fields) ? fields : [])
    .reduce((mapping, field) => {
        mapping[field] = { value: field };
        return mapping;
    }, {});

// Duplicate serials within one file, ignoring blanks. Blank serials are legitimate and
// are kept in file order (P1-12) -- the old check hashed them all to '' and reported the
// second blank row as a duplicate, which blocked the whole import.
//
// P1-4 (2026-08-05): this now normalises through serialKey, the SAME rule the scan screen
// and the server use (strip all whitespace + uppercase). It used to trim only, which after
// decision 1 would have been LOOSER than the server: a file containing both `ABC 123` and
// `ABC123` would have passed this pre-check, rendered an Import button, and then been
// rejected by the server. Blocking it here, up front, is the better failure.
const duplicateSerials = (rows) => {
    const seen = new Set();
    const duplicates = new Set();
    (Array.isArray(rows) ? rows : []).forEach((row) => {
        const raw = String(row?.serial_number ?? '').trim();
        const key = serialKey(raw);
        if (key === '') return;
        // The value echoed back is what the operator typed, because they have to find it
        // in their spreadsheet -- not the normalised form.
        if (seen.has(key)) duplicates.add(raw);
        else seen.add(key);
    });
    return [...duplicates];
};

const csvCell = (value) => {
    const text = String(value ?? '');
    return /[",\r\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
};

// A blank template plus one example row, so the expected format is self-evident.
const buildTemplateCsv = () => {
    const header = INTENT_LIST_COLUMNS.map(c => csvCell(c.header)).join(',');
    const example = INTENT_LIST_COLUMNS.map(c => csvCell(c.example)).join(',');
    return `${header}\r\n${example}\r\n`;
};

const TEMPLATE_FILENAME = 'CompuCycle_Intent_List_Template.csv';

const exports = {
    INTENT_LIST_COLUMNS,
    REQUIRED_FIELDS,
    HEADER_ALIASES,
    TEMPLATE_FILENAME,
    normalizeHeader,
    fieldForHeader,
    findHeaderRow,
    parseIntentRows,
    mappingForFields,
    duplicateSerials,
    buildTemplateCsv,
};

export default exports;
export {
    INTENT_LIST_COLUMNS,
    REQUIRED_FIELDS,
    HEADER_ALIASES,
    TEMPLATE_FILENAME,
    normalizeHeader,
    fieldForHeader,
    findHeaderRow,
    parseIntentRows,
    mappingForFields,
    duplicateSerials,
    buildTemplateCsv,
};
